/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, type ReactNode, useEffect, useRef, useState } from 'react';

import { cssMap as unboundedCssMap } from '@compiled/react';

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

export interface PanelSubheaderProps {
	/**
	 * The title text to display in the subheader.
	 */
	title: string;
	/**
	 * An optional cover image to display above the subheader title.
	 * Accepts any React node — typically an `<img>` element, a media component,
	 * or a custom image wrapper.
	 *
	 * The image fills the full width of the panel and is cropped to a fixed height of 160px.
	 *
	 * Accessibility: any `<img>` passed here must include a meaningful `alt` attribute describing the
	 * image, or `alt=""` if it is purely decorative, to satisfy WCAG 1.1.1 (Non-text Content). This
	 * is not enforced at the type level, so consumers are responsible for providing it.
	 */
	coverImage?: ReactNode;
	/**
	 * An optional breadcrumbs element to display directly above the title.
	 * Designed for the `@atlaskit/breadcrumbs` component (small variant), which is 24px tall.
	 * The slot reserves a 24px-tall row and applies the correct gap between the breadcrumbs and title.
	 */
	breadcrumbs?: ReactNode;
	/**
	 * An optional icon to display at the start of the title.
	 * Accepts any React node — typically an `@atlaskit/icon` component.
	 */
	titleIcon?: ReactNode;
	/**
	 * An optional inline edit control to render in place of the static title, allowing the title to
	 * be edited in place. Typically a `SubheaderInlineEdit`, which is pre-styled to match the title.
	 *
	 * When provided, this is rendered instead of the `title` text. The `title` prop should still be
	 * supplied as the current value passed to the inline edit control.
	 */
	inlineEdit?: ReactNode;
	/**
	 * Optional content shown after the regular subheader scrolls out of view.
	 * Accepts any React node. Content is vertically centered in a 2rem-high slot, with
	 * 8px bottom padding, no top padding, and 24px inline padding around the slot.
	 * Consumers are responsible for content sizing and text truncation.
	 * Sticky state, refs, and observation are only mounted when this content is provided.
	 *
	 * `PanelSubheader` must be a direct child of `PanelContent` for the sticky positioning to work.
	 */
	stickyContent?: ReactNode;
	/**
	 * A unique string that appears as data attribute `data-testid` in the rendered code, serving as a hook for automated tests.
	 */
	testId?: string;
}

const styles = cssMap({
	subheader: {
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
	},
	breadcrumbsSlot: {
		display: 'flex',
		alignItems: 'center',
		minHeight: '24px',
	},
	stickyContainer: {
		position: 'sticky',
		insetBlockStart: token('space.0'),
		height: 0,
		zIndex: 1,
	},
	stickyContentSlot: {
		height: '2rem',
		minWidth: 0,
	},
});

const unboundedStyles = unboundedCssMap({
	// Center the leading title element in a 24x24 slot, cap a larger element to that slot so it does
	// not overflow or push the title, and keep the slot from shrinking next to a long title. The cap
	// uses max-width/height (not fixed) so a smaller icon stays its natural size and is centered,
	// rather than being stretched to 24px and pinned to the top-left.
	titleIconConstrained: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '1.5rem',
		height: '1.5rem',
		flexShrink: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
		'& > *': {
			maxWidth: '1.5rem',
			maxHeight: '1.5rem',
		},
	},
	coverImageContainer: {
		marginInlineStart: token('space.negative.300'),
		marginInlineEnd: token('space.negative.300'),
		height: '160px',
		overflow: 'hidden',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
		'& > *': {
			display: 'block',
			width: '100%',
			height: '100%',
			objectFit: 'cover',
		},
	},
	stickyContent: {
		backgroundColor: token('elevation.surface'),
		paddingBlockStart: token('space.0'),
		paddingBlockEnd: token('space.100'),
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
	},
});

function StickySubheader({
	children,
	testId,
}: {
	children: ReactNode;
	testId?: string;
}): JSX.Element {
	const stickyContainerRef = useRef<HTMLDivElement>(null);
	const [isSticky, setIsSticky] = useState(false);

	useEffect(() => {
		// PanelSubheader renders the regular subheader immediately before this sibling.
		// Both must be direct children of PanelContent, which is their scroll container.
		const stickyContainer = stickyContainerRef.current;
		const subheader = stickyContainer?.previousElementSibling;
		const scrollContainer = stickyContainer?.parentElement;

		if (!subheader || !scrollContainer || typeof IntersectionObserver === 'undefined') {
			return;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				const scrollContainerTop =
					entry.rootBounds?.top ?? scrollContainer.getBoundingClientRect().top;

				setIsSticky(!entry.isIntersecting && entry.boundingClientRect.bottom <= scrollContainerTop);
			},
			{ root: scrollContainer },
		);

		observer.observe(subheader);

		return () => observer.disconnect();
	}, []);

	return (
		<Box ref={stickyContainerRef} xcss={styles.stickyContainer}>
			<div
				hidden={!isSticky}
				css={unboundedStyles.stickyContent}
				data-testid={testId ? `${testId}-sticky` : undefined}
			>
				<Flex alignItems="center" xcss={styles.stickyContentSlot}>
					{children}
				</Flex>
			</div>
		</Box>
	);
}

/**
 * The PanelSubheader component provides a slot for subheaders with a title,
 * and an optional cover image that fills the panel width above the title.
 */
export function PanelSubheader({
	title,
	titleIcon,
	coverImage,
	breadcrumbs,
	inlineEdit,
	stickyContent,
	testId,
}: PanelSubheaderProps): JSX.Element {
	const subheader = (
		<Flex testId={testId} direction="column" gap="space.150" xcss={styles.subheader}>
			{coverImage && <div css={unboundedStyles.coverImageContainer}>{coverImage}</div>}
			<Flex direction="column" gap="space.100">
				{breadcrumbs && <Box xcss={styles.breadcrumbsSlot}>{breadcrumbs}</Box>}
				<Flex alignItems="start" gap="space.150">
					{titleIcon && <div css={unboundedStyles.titleIconConstrained}>{titleIcon}</div>}
					{inlineEdit ?? (
						<Heading as="h3" size="medium">
							{title}
						</Heading>
					)}
				</Flex>
			</Flex>
		</Flex>
	);

	if (stickyContent === undefined || stickyContent === null || stickyContent === false) {
		return subheader;
	}

	return (
		<Fragment>
			{subheader}
			<StickySubheader testId={testId}>{stickyContent}</StickySubheader>
		</Fragment>
	);
}
